import type {
	PartnerAvailabilityResponseDto,
	PartnerAvailabilityUpdateItemDto,
} from "Infrastructure/Api/Api";
import { getMessageFromErrorResponse } from "Infrastructure/Api/ApiUtils";
import { useUpdatePartnerAvailabilityMutation } from "Partner/Api/Mutation/useUpdatePartnerAvailabilityMutation";
import { usePartnerAvailability } from "Partner/Api/Query/usePartnerAvailabilityQuery";
import { Legend } from "Partner/Components/Calendar/Legend";
import {
	PARTNER_CALENDAR_STATUS_COLORS,
	PARTNER_CALENDAR_STATUS_LABELS,
	PartnerCalendarStatus,
} from "Partner/Components/Calendar/types";
import { ChevronLeft, ChevronRight, Save } from "@mui/icons-material";
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { toast } from "sonner";

type PartnerAvailabilityTabProps = {
	partnerId: number;
};

export const PartnerAvailabilityTab = ({
	partnerId,
}: PartnerAvailabilityTabProps) => {
	const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
	const [modifiedAvailability, setModifiedAvailability] = useState<
		Map<string, PartnerCalendarStatus>
	>(new Map());

	const daysInMonth = useMemo(() => {
		const startOfMonth = currentMonth.startOf("month");
		const endOfMonth = currentMonth.endOf("month");
		const days: Dayjs[] = [];

		// Add padding days from previous month
		const startDayOfWeek = startOfMonth.day(); // 0 (Sunday) - 6 (Saturday)
		// Adjust for Monday start (0=Monday, 6=Sunday)
		const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

		for (let i = adjustedStartDay; i > 0; i--) {
			days.push(startOfMonth.subtract(i, "day"));
		}

		// Add days of current month
		for (let i = 0; i < endOfMonth.date(); i++) {
			days.push(startOfMonth.add(i, "day"));
		}

		// Add padding days for next month to complete the grid (optional, but looks better)
		const remainingCells = 42 - days.length; // 6 rows * 7 columns
		for (let i = 1; i <= remainingCells; i++) {
			days.push(endOfMonth.add(i, "day"));
		}

		return days;
	}, [currentMonth]);

	const queryStart = daysInMonth[0]?.format("YYYY-MM-DD");
	const queryEnd = daysInMonth[daysInMonth.length - 1]?.format("YYYY-MM-DD");

	const {
		data: availabilityList,
		isLoading,
		refetch,
	} = usePartnerAvailability(partnerId, queryStart, queryEnd);

	const { mutateAsync: updateAvailabilityAsync, isPending: isSaving } =
		useUpdatePartnerAvailabilityMutation(partnerId);

	const availabilityMap = useMemo(() => {
		const map = new Map<string, { status: string; isBlocked?: boolean }>();
		if (availabilityList) {
			for (const item of availabilityList as PartnerAvailabilityResponseDto[]) {
				// Normalize date to YYYY-MM-DD
				const dateStr = dayjs(item.date).format("YYYY-MM-DD");
				map.set(dateStr, { status: item.status, isBlocked: item.isBlocked });
			}
		}
		return map;
	}, [availabilityList]);

	const handlePrevMonth = () => {
		setCurrentMonth(currentMonth.subtract(1, "month"));
	};

	const handleNextMonth = () => {
		setCurrentMonth(currentMonth.add(1, "month"));
	};

	const handleToggleAvailability = (
		date: string,
		currentStatus: PartnerCalendarStatus,
	) => {
		const newStatus =
			currentStatus === PartnerCalendarStatus.AVAILABLE
				? PartnerCalendarStatus.NOT_AVAILABLE
				: PartnerCalendarStatus.AVAILABLE;
		setModifiedAvailability((prev) => {
			const next = new Map(prev);
			next.set(date, newStatus);
			return next;
		});
	};

	const handleSave = () => {
		const items: PartnerAvailabilityUpdateItemDto[] = Array.from(
			modifiedAvailability,
		).map(([date, status]) => ({
			partnerId,
			date,
			status:
				status === PartnerCalendarStatus.AVAILABLE
					? "available"
					: "not_available",
		}));

		if (items.length === 0) return;

		toast.promise(
			updateAvailabilityAsync({ items }).then(() => {
				setModifiedAvailability(new Map());
				refetch();
			}),
			{
				loading: "Updating availability...",
				success: "Availability updated successfully",
				error: (error) =>
					`Failed to update availability: ${getMessageFromErrorResponse(error)}`,
			},
		);
	};

	const hasChanges = modifiedAvailability.size > 0;

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" p={4}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box p={{ xs: 1, md: 3 }}>
			<Paper
				elevation={0}
				variant="outlined"
				sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}
			>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					justifyContent="space-between"
					alignItems="center"
					mb={3}
					spacing={2}
				>
					<Stack direction="row" alignItems="center" spacing={1}>
						<IconButton onClick={handlePrevMonth}>
							<ChevronLeft />
						</IconButton>
						<Typography variant="h6" fontWeight="bold">
							{currentMonth.format("MMMM YYYY")}
						</Typography>
						<IconButton onClick={handleNextMonth}>
							<ChevronRight />
						</IconButton>
					</Stack>
					<Stack direction="row" spacing={2} alignItems="center">
						<Legend />
						<Button
							variant="contained"
							startIcon={isSaving ? <CircularProgress size={20} /> : <Save />}
							onClick={handleSave}
							disabled={!hasChanges || isSaving}
						>
							Save Changes
						</Button>
					</Stack>
				</Stack>

				<Box sx={{ overflowX: "auto" }}>
					<Box
						display="grid"
						gridTemplateColumns="repeat(7, 1fr)"
						gap={1}
						sx={{ minWidth: 600 }} // Ensure calendar doesn't get too squashed
					>
						{/* Weekday Headers */}
						{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
							<Box key={day}>
								<Box
									textAlign="center"
									py={1}
									fontWeight="bold"
									color="text.secondary"
									fontSize={{ xs: "0.8rem", md: "1rem" }}
								>
									{day}
								</Box>
							</Box>
						))}

						{/* Calendar Days */}
						{daysInMonth.map((day) => {
							const dateStr = day.format("YYYY-MM-DD");
							// Get explicit status or default to NOT_AVAILABLE if missing (Single Source of Truth: Absence = Not Available)
							const availability = availabilityMap.get(dateStr);
							const pending = modifiedAvailability.get(dateStr);
							const status: PartnerCalendarStatus =
								pending ??
								(availability?.status as PartnerCalendarStatus) ??
								PartnerCalendarStatus.NOT_AVAILABLE;
							const isModified = pending !== undefined;
							const isBlocked = availability?.isBlocked ?? true;
							// Recompute blocked for display when a pending change exists
							const displayIsBlocked = pending
								? pending === PartnerCalendarStatus.NOT_AVAILABLE
								: isBlocked;
							const isCurrentMonth = day.month() === currentMonth.month();
							const isToday = day.isSame(dayjs(), "day");
							const isEditable =
								isCurrentMonth && status !== PartnerCalendarStatus.ADAM;

							let bgColor = PARTNER_CALENDAR_STATUS_COLORS[status] || "#e5e7eb";
							if (displayIsBlocked) {
								bgColor = "#7f1d1d"; // Red-900 for blocked
							}
							const textColor = "#fff"; // Always white since all statuses now have background color
							const label = PARTNER_CALENDAR_STATUS_LABELS[status] || status;

							return (
								<Box key={dateStr}>
									<Box
										sx={{
											height: { xs: 60, md: 80 },
											border: "1px solid",
											borderColor: isToday ? "primary.main" : "divider",
											borderRadius: 1,
											p: 0.5,
											bgcolor: isCurrentMonth
												? "background.paper"
												: "action.hover",
											opacity: isCurrentMonth ? 1 : 0.5,
											position: "relative",
											display: "flex",
											flexDirection: "column",
											justifyContent: "space-between",
											boxShadow: isModified
												? "inset 0 0 0 2px #f59e0b"
												: undefined,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={isToday ? "bold" : "normal"}
											color={isToday ? "primary.main" : "text.secondary"}
										>
											{day.format("D")}
										</Typography>

										<Box
											onClick={
												isEditable
													? () => handleToggleAvailability(dateStr, status)
													: undefined
											}
											sx={{
												bgcolor: bgColor,
												color: textColor,
												borderRadius: 0.5,
												px: 0.5,
												py: 0.25,
												fontSize: { xs: "0.6rem", md: "0.7rem" },
												textAlign: "center",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
												cursor: isEditable ? "pointer" : "default",
												"&:hover": isEditable
													? { filter: "brightness(0.85)" }
													: undefined,
											}}
										>
											{label}
										</Box>
									</Box>
								</Box>
							);
						})}
					</Box>
				</Box>
			</Paper>
		</Box>
	);
};
